import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import StoreNavigation from '../components/StoreNavigation';
import StoreFooter from '../components/StoreFooter';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useCart } from '../context/CartContext';
import { MapPin, Search, Phone, Globe, ArrowRight, Store } from 'lucide-react';

const Stockists = () => {
  const { getCartItemCount } = useCart();
  const [searchPostcode, setSearchPostcode] = useState('');
  const [searchResults, setSearchResults] = useState(null);

  // Stockist data - add stockists here as they sign up
  const stockists = [
    // Example structure:
    // {
    //   name: 'ABC Collision Supplies',
    //   address: '123 Main Street, Sydney NSW 2000',
    //   postcode: '2000',
    //   state: 'NSW',
    //   phone: '02 9999 9999',
    //   website: 'https://example.com',
    //   type: 'Supplier' // or 'Collision Repair Shop', 'Training Center'
    // }
  ];

  // Australian postcode to state mapping (first digit)
  const getStateFromPostcode = (postcode) => {
    const firstDigit = postcode.charAt(0);
    const stateMap = {
      '1': 'NSW',
      '2': 'NSW',
      '3': 'VIC',
      '4': 'QLD',
      '5': 'SA',
      '6': 'WA',
      '7': 'TAS',
      '0': 'NT',
    };
    // ACT is 02xx
    if (postcode.startsWith('02')) return 'ACT';
    return stateMap[firstDigit] || null;
  };

  const handleSearch = (e) => {
    e.preventDefault();
    
    if (!searchPostcode || searchPostcode.length < 4) {
      setSearchResults([]);
      return;
    }

    const searchState = getStateFromPostcode(searchPostcode);
    
    // Filter stockists by state, then sort by postcode proximity
    const results = stockists
      .filter(s => s.state === searchState)
      .sort((a, b) => {
        const diffA = Math.abs(parseInt(a.postcode) - parseInt(searchPostcode));
        const diffB = Math.abs(parseInt(b.postcode) - parseInt(searchPostcode));
        return diffA - diffB;
      });

    setSearchResults(results);
  };

  // Group stockists by state for the full list
  const stockistsByState = stockists.reduce((acc, stockist) => {
    if (!acc[stockist.state]) acc[stockist.state] = [];
    acc[stockist.state].push(stockist);
    return acc;
  }, {});

  const stateNames = {
    'NSW': 'New South Wales',
    'VIC': 'Victoria',
    'QLD': 'Queensland',
    'SA': 'South Australia',
    'WA': 'Western Australia',
    'TAS': 'Tasmania',
    'NT': 'Northern Territory',
    'ACT': 'Australian Capital Territory'
  };

  return (
    <div className="min-h-screen bg-white">
      <StoreNavigation cartItemCount={getCartItemCount()} />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#004D51] to-[#003338] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <MapPin className="w-16 h-16 mx-auto mb-6 text-[#F39C49]" />
            <h1 className="text-5xl md:text-7xl font-black mb-6">
              Find a <span className="text-[#F39C49]">Stockist</span>
            </h1>
            <p className="text-xl text-[#CFD2D4]">
              Prefer to buy locally? Find an authorised Vesta stockist near you.
            </p>
          </div>
        </div>
      </section>

      {/* Postcode Search */}
      <section className="py-12 bg-[#F3CEB2]/20">
        <div className="container mx-auto px-6">
          <form onSubmit={handleSearch} className="max-w-xl mx-auto">
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Input
                  type="text"
                  value={searchPostcode}
                  onChange={(e) => setSearchPostcode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="Enter your postcode"
                  className="h-14 pl-12 text-lg border-2 border-[#004D51] focus:border-[#DB5A14]"
                  data-testid="stockist-postcode-input"
                />
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#545759]" />
              </div>
              <Button 
                type="submit"
                className="h-14 px-8 bg-[#DB5A14] hover:bg-[#F39C49] text-white font-black"
                data-testid="stockist-search-btn"
              >
                <Search className="w-5 h-5 mr-2" />
                Search
              </Button>
            </div>
          </form>
        </div>
      </section>

      <div className="container mx-auto px-6 py-16">
        {/* Search Results */}
        {searchResults !== null && (
          <section className="mb-16">
            <h2 className="text-2xl font-black text-[#004D51] mb-6">
              {searchResults.length > 0 
                ? `${searchResults.length} Stockist${searchResults.length > 1 ? 's' : ''} Near ${searchPostcode}`
                : `No Stockists Found Near ${searchPostcode}`
              }
            </h2>
            
            {searchResults.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-6">
                {searchResults.map((stockist, index) => (
                  <StockistCard key={index} stockist={stockist} />
                ))}
              </div>
            ) : (
              <div className="bg-[#F3CEB2]/20 rounded-xl p-8 text-center">
                <p className="text-[#545759] mb-4">
                  We don't have a stockist in your area yet, but you can order directly from us!
                </p>
                <Link to="/shop">
                  <Button className="bg-[#DB5A14] hover:bg-[#F39C49] text-white font-black">
                    Shop Online <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            )}
          </section>
        )}

        {/* All Stockists by State */}
        {stockists.length > 0 ? (
          <section>
            <h2 className="text-4xl font-black text-[#004D51] mb-8 text-center">
              All Stockists
            </h2>
            
            {Object.entries(stockistsByState).map(([state, stateStockists]) => (
              <div key={state} className="mb-12">
                <h3 className="text-2xl font-black text-[#004D51] mb-6 pb-2 border-b-4 border-[#F39C49]">
                  {stateNames[state] || state}
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  {stateStockists.map((stockist, index) => (
                    <StockistCard key={index} stockist={stockist} />
                  ))}
                </div>
              </div>
            ))}
          </section>
        ) : (
          <section className="text-center py-12">
            <div className="w-24 h-24 bg-[#F3CEB2]/30 rounded-full flex items-center justify-center mx-auto mb-8">
              <Store className="w-12 h-12 text-[#004D51]" />
            </div>
            <h2 className="text-3xl font-black text-[#004D51] mb-4">
              Stockist Network Coming Soon
            </h2>
            <p className="text-xl text-[#545759] mb-8 max-w-2xl mx-auto">
              We're building our network of authorised stockists across Australia. 
              In the meantime, shop directly with us online.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/shop">
                <Button className="bg-[#DB5A14] hover:bg-[#F39C49] text-white font-black px-8 py-4">
                  Shop Online <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/wholesale">
                <Button variant="outline" className="border-2 border-[#004D51] text-[#004D51] hover:bg-[#004D51] hover:text-white font-black px-8 py-4">
                  Become a Stockist
                </Button>
              </Link>
            </div>
          </section>
        )}

        {/* Become a Stockist CTA */}
        {stockists.length > 0 && (
          <section className="mt-16 bg-[#004D51] rounded-2xl p-12 text-center text-white">
            <Store className="w-14 h-14 mx-auto mb-6 text-[#F39C49]" />
            <h2 className="text-4xl font-black mb-4">
              Want to Stock Vesta?
            </h2>
            <p className="text-xl text-[#CFD2D4] mb-8 max-w-2xl mx-auto">
              Join our growing network of authorised stockists across Australia.
            </p>
            <Link to="/wholesale">
              <Button className="bg-[#DB5A14] hover:bg-[#F39C49] text-white font-black px-8 py-6 text-lg">
                Apply for Trade Account <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </section>
        )}
      </div>

      <StoreFooter />
    </div>
  );
};

// Stockist Card Component
const StockistCard = ({ stockist }) => {
  return (
    <div className="bg-white border-4 border-[#004D51] rounded-xl p-6 hover:shadow-lg transition-all">
      <div className="flex items-start justify-between mb-3">
        <h4 className="text-xl font-black text-[#004D51]">{stockist.name}</h4>
        {stockist.type && (
          <span className="text-xs font-bold bg-[#F3CEB2] text-[#004D51] px-2 py-1 rounded">
            {stockist.type}
          </span>
        )}
      </div>
      
      <p className="text-[#545759] mb-4 flex items-start">
        <MapPin className="w-4 h-4 mr-2 mt-1 flex-shrink-0 text-[#DB5A14]" />
        {stockist.address}
      </p>
      
      <div className="flex flex-wrap gap-4">
        {stockist.phone && (
          <a 
            href={`tel:${stockist.phone}`}
            className="flex items-center text-[#004D51] hover:text-[#DB5A14] font-medium"
          >
            <Phone className="w-4 h-4 mr-1" />
            {stockist.phone}
          </a>
        )}
        {stockist.website && (
          <a 
            href={stockist.website}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center text-[#004D51] hover:text-[#DB5A14] font-medium"
          >
            <Globe className="w-4 h-4 mr-1" />
            Website
          </a>
        )}
      </div>
    </div>
  );
};

export default Stockists;
